import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { SlideOverProps } from './types';

import { useTranslation } from 'react-i18next';

declare global {
  interface WindowEventMap {
    'pleri-render-change': CustomEvent<string>;
  }
}

export const SlideOver = ({
  isOpen,
  setIsOpen,
  overlay,
  disableCloseOnOverlayClick,
  children,
  slideOverClassName = 'max-w-2xl bg-white dark:bg-gray-900',
  mfaId = 'main-app',
  style,
}: SlideOverProps) => {
  const { t } = useTranslation('designSystem');
  const dialogRef = useRef<HTMLDivElement>(null);
  const dialogPanelRef = useRef<HTMLDivElement>(null);
  const disablePointerEventsClass = overlay ? '' : 'pointer-events-none';
  const [isPleriOpen, setIsPleriOpen] = useState(false);

  // TODO Replace this callback with Dialog.onClose once we upgrade to Headless UI v2
  //   with v1, the onclose callback is called for all dialogs instead of just the top dialog
  const handleEscapeKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (!isOpen) {
          return;
        }
        if (!dialogRef.current) {
          return;
        }
        const isTopDialog = document
          .querySelector('#headlessui-portal-root > :last-child')
          ?.contains(dialogRef.current);

        if (isTopDialog) {
          setIsOpen(false);
          event.stopPropagation();
          event.preventDefault();
        }
      }
    },
    [isOpen, setIsOpen],
  );

  const handlePleriChatEvent = useCallback(
    (event: CustomEvent<string>) => {
      setIsPleriOpen(event.detail !== 'closed');
    },
    [setIsPleriOpen],
  );

  useEffect(() => {
    const pleriMiniLayout = document.getElementById('pleri-mini-layout');
    if (pleriMiniLayout) {
      setIsPleriOpen(true);
    }

    window.addEventListener('pleri-render-change', handlePleriChatEvent);

    return () => {
      window.removeEventListener('pleri-render-change', handlePleriChatEvent);
    };
  }, [handlePleriChatEvent]);

  useEffect(() => {
    document.addEventListener('keydown', handleEscapeKeyDown, false);

    return () => {
      document.removeEventListener('keydown', handleEscapeKeyDown, false);
    };
  }, [handleEscapeKeyDown]);

  return (
    <Transition show={isOpen} as={Fragment} appear>
      <Dialog as="div" onClose={noAction} ref={dialogRef}>
        <div className={process.env.APP_NAME || mfaId} id={mfaId}>
          <div className="relative z-[101]">
            {overlay && (
              <TransitionChild appear as={Fragment}>
                <div className="bg-overlay-base/30 fixed inset-0 z-[101] transition duration-300 data-closed:opacity-0" />
              </TransitionChild>
            )}

            <div
              className={`dialog-overlay fixed inset-0 z-[101] overflow-hidden ${disablePointerEventsClass}`}
              onClick={(event) => {
                if (disableCloseOnOverlayClick) {
                  return;
                }
                if (!dialogRef.current || !dialogPanelRef.current) {
                  return;
                }
                const isTopDialog = document
                  .querySelector('#headlessui-portal-root > :last-child')
                  ?.contains(dialogRef.current);
                const isClickInsideDialog = dialogRef.current.contains(
                  event.target as Node,
                );
                const isClickInsideDialogPanel =
                  dialogPanelRef.current.contains(event.target as Node);
                if (
                  isTopDialog &&
                  isClickInsideDialog &&
                  !isClickInsideDialogPanel
                ) {
                  setIsOpen(false);
                  event.stopPropagation();
                  event.preventDefault();
                }
              }}
            >
              <div
                className={`absolute inset-0 overflow-hidden ${disablePointerEventsClass}`}
              >
                <div
                  className={`pointer-events-none fixed inset-y-0 flex max-w-full pl-10 transition-[right] duration-300 ease-in-out sm:pl-16`}
                  style={{ marginTop: 0, right: isPleriOpen ? 472 : 0 }} // 472px = 420px (pleri-panel) + 52px (pleri-sidebar)
                >
                  <TransitionChild as={Fragment} appear>
                    <DialogPanel
                      className={`pointer-events-auto relative w-screen transform border-l shadow transition duration-300 ease-in-out data-closed:translate-x-full ${slideOverClassName}`}
                      style={{
                        ...style,
                        maxWidth: isPleriOpen
                          ? 'calc(90vw - 472px - 4rem)' // 472px = 420px (pleri-panel) + 52px (pleri-sidebar) - 4rem (padding)
                          : '80rem', // Explicit value needed for CSS transition
                      }}
                      as="div"
                      ref={dialogPanelRef}
                      data-testid="contextual-view-panel"
                    >
                      <TransitionChild
                        as={Fragment}
                        enter="ease-in-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in-out duration-300"
                        leaveFrom="opacity-100"
                        appear
                        leaveTo="opacity-0"
                      >
                        <div className="slide-over-close absolute top-0 left-0 -ml-10 flex pt-4">
                          <button
                            type="button"
                            className="hover:text-br-primary focus:ring-br-primary rounded-l-md border border-r-0 bg-white dark:bg-gray-900 p-2 text-gray-500 dark:text-gray-400 focus:ring-2 focus:outline-none"
                            onClick={() => setIsOpen(false)}
                          >
                            <span
                              data-cy="slide-over-close-panel"
                              data-testid="slide-over-close-panel"
                              className="sr-only"
                            >
                              {t('closePanel')}
                            </span>
                            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                          </button>
                        </div>
                      </TransitionChild>
                      {children}
                    </DialogPanel>
                  </TransitionChild>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
const noAction = () => undefined;
